// src/components/pages/worker/dialogs/WorkerAusenciaDialog.tsx

import { useState, useMemo } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

import {
  useTiposAusencia,
  useAusenciasPorEmpresa,
} from '@/components/hooks/useAusencias';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rangoSeleccionado: [Date, Date] | null;
  empresaId: string;
  userId: string;
  diasFestivos?: string[];
  diasNoLaborables?: string[];
  onNuevaAusencia?: (nuevas: any[]) => void;
}

const toISO = (d: Date) => d.toLocaleDateString('sv-SE');

const WorkerAusenciaDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  rangoSeleccionado,
  empresaId,
  userId,
  diasFestivos = [],
  diasNoLaborables = [],
  onNuevaAusencia,
}) => {
  const tiposAusencia = useTiposAusencia();
  const { crearAusencia } = useAusenciasPorEmpresa(empresaId);

  const [tipoSeleccionado, setTipoSeleccionado] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const diasExcluidos = useMemo(
    () => [...diasFestivos, ...diasNoLaborables],
    [diasFestivos, diasNoLaborables]
  );

  const diffDays = useMemo(() => {
    if (!rangoSeleccionado) return 0;
    const [inicio, fin] = rangoSeleccionado;

    let count = 0;
    const cur = new Date(inicio);
    while (cur <= fin) {
      if (!diasExcluidos.includes(toISO(cur))) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  }, [rangoSeleccionado, diasExcluidos]);

  const handleEnviar = async () => {
    if (!rangoSeleccionado || !tipoSeleccionado) return;
    setEnviando(true);
    try {
      const [inicio, fin] = rangoSeleccionado;
      const result = await crearAusencia({
        idUsuario: userId,
        idEmpresa: empresaId,
        idTipoAusencia: tipoSeleccionado,
        fechaInicio: inicio,
        fechaFin: fin,
        diasFestivos: diasExcluidos,
      });

      if (!result.ok) throw new Error();
      if (onNuevaAusencia && result.dias) onNuevaAusencia(result.dias);

      toast.success('Solicitud enviada correctamente');
      onOpenChange(false);
    } catch {
      toast.error('No se pudo registrar la ausencia');
    } finally {
      setEnviando(false);
    }
  };

  if (!rangoSeleccionado) return null;
  const [inicio, fin] = rangoSeleccionado;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
        "
      >
        <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
              Solicitar ausencia
            </DialogTitle>
          </DialogHeader>
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 sm:px-6 pb-5">
          <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4 text-sm">
            <div className="space-y-1">
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Desde:</strong> {inicio.toLocaleDateString('es-ES')}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Hasta:</strong> {fin.toLocaleDateString('es-ES')}
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Total:</strong> {diffDays} día{diffDays > 1 ? 's' : ''}
                {diasExcluidos.length > 0 && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {' '}
                    (festivos y no laborables no contabilizados)
                  </span>
                )}
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Tipo de ausencia
              </label>
              <Select
                value={tipoSeleccionado || ''}
                onValueChange={(v) => setTipoSeleccionado(v)}
              >
                <SelectTrigger className="h-9 text-sm focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
                  <SelectValue placeholder="Selecciona un tipo" />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  {tiposAusencia.map((t) => (
                    <SelectItem
                      key={t.id_tipo_ausencia}
                      value={t.id_tipo_ausencia}
                      className="text-sm"
                    >
                      {t.tipo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEnviar}
            disabled={!tipoSeleccionado || enviando}
            className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
          >
            {enviando ? 'Enviando…' : 'Confirmar solicitud'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkerAusenciaDialog;